import classNames from 'classnames';
import React, { useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  GIFT_CARD_MESSAGE_MAX_LENGTH,
  giftCardDeliverAt,
  giftCardDeliveryDay,
  giftCardDeliveryDays,
  giftCardDeliveryErrors,
} from '../../lib/giftRecipient';
import { GiftCardDelivery } from '../../types';
import styles from './GiftOptions.module.scss';

interface GiftCardFieldsProps {
  delivery: GiftCardDelivery;
  onChange: (delivery: GiftCardDelivery) => void;
  /** an empty email is only an error once the shopper has tried to pay */
  showErrors: boolean;
  /** the card's formatted amount, heading the fields when a tracker shares the order */
  amount?: string;
}

type TextField = 'firstName' | 'emailAddress' | 'gifterName';

export default function GiftCardFields({ delivery, onChange, showErrors, amount }: GiftCardFieldsProps) {
  const { t } = useTranslation();
  const id = useId();
  // typed text reaches the cart on blur, since every cart change re-prices the checkout
  const [draft, setDraft] = useState(delivery);
  const [choosingDate, setChoosingDate] = useState(!!delivery.deliverAt);
  const commit = () => draft !== delivery && onChange(draft);
  // enter submits the form without a blur, so it commits too
  const commitOn = { onBlur: commit, onKeyDown: (event: React.KeyboardEvent) => event.key === 'Enter' && commit() };
  const errors = giftCardDeliveryErrors(draft);
  const { min, max } = giftCardDeliveryDays();
  const message = draft.personalMessage ?? '';
  const emailError = showErrors && errors.emailAddress ? t(errors.emailAddress) : undefined;
  const sendNow = () => {
    setChoosingDate(false);
    if (!draft.deliverAt) return;
    const next = { ...draft, deliverAt: null };
    setDraft(next);
    onChange(next);
  };

  const row = (labelKey: string, field: TextField, placeholderKey: string, type = 'text') => (
    <label className={styles.row}>
      <span className={styles.rowKey}>{t(labelKey)}</span>
      <input
        aria-describedby={field === 'emailAddress' && emailError ? `${id}-email-error` : undefined}
        aria-invalid={field === 'emailAddress' && !!emailError}
        autoComplete="off"
        className={styles.rowValue}
        onChange={(event) => setDraft({ ...draft, [field]: event.currentTarget.value })}
        placeholder={t(placeholderKey)}
        required={field === 'emailAddress'}
        type={type}
        value={draft[field] ?? ''}
        {...commitOn}
      />
    </label>
  );

  const whenOption = (labelKey: string, checked: boolean, onSelect: () => void) => (
    <label className={classNames(styles.whenOption, { [styles.on]: checked })}>
      <input checked={checked} className={styles.radioInput} name={`${id}-when`} onChange={onSelect} type="radio" />
      {t(labelKey)}
    </label>
  );

  return (
    <>
      {amount && (
        <div className={styles.itemHead}>
          <span aria-hidden="true" className={styles.giftMini} />
          <span className={styles.itemText}>
            <b>{t('hardware_first_checkout_gift_card_item_title', { amount })}</b>
            <span>{t('hardware_first_checkout_gift_card_item_sub')}</span>
          </span>
        </div>
      )}
      <div className={styles.block}>
        <h4 className={styles.blockTitle}>{t('gift_card_who_is_it_for')}</h4>
        {row('name', 'firstName', 'gift_card_recipient_name_placeholder')}
        {row('email', 'emailAddress', 'gift_card_recipient_email_placeholder', 'email')}
        {emailError && (
          <p className={styles.error} id={`${id}-email-error`}>
            {emailError}
          </p>
        )}
      </div>
      <div className={styles.block}>
        <h4 className={styles.blockTitle}>{t('gift_card_who_is_it_from')}</h4>
        {row('name', 'gifterName', 'gift_card_sender_name_placeholder')}
      </div>
      <div className={styles.block}>
        <h4 className={styles.blockTitle}>
          <label htmlFor={`${id}-message`}>{t('message')}</label>
        </h4>
        <textarea
          aria-invalid={!!errors.personalMessage}
          className={classNames(styles.input, styles.message)}
          id={`${id}-message`}
          maxLength={GIFT_CARD_MESSAGE_MAX_LENGTH}
          onBlur={commit}
          onChange={(event) => setDraft({ ...draft, personalMessage: event.currentTarget.value })}
          placeholder={t('type_in_any_message_you_want_delivered_with_the_gift')}
          value={message}
        />
        <p className={styles.left}>
          {t('characters_remaining', { count: GIFT_CARD_MESSAGE_MAX_LENGTH - message.length })}
        </p>
        {errors.personalMessage && (
          <p className={styles.error}>{t(errors.personalMessage, { max: GIFT_CARD_MESSAGE_MAX_LENGTH })}</p>
        )}
      </div>
      <div className={styles.block}>
        <h4 className={styles.blockTitle} id={`${id}-when`}>
          {t('delivery_date_label')}
        </h4>
        <div aria-labelledby={`${id}-when`} className={styles.when} role="radiogroup">
          {whenOption('now', !choosingDate, sendNow)}
          {whenOption('choose_date', choosingDate, () => setChoosingDate(true))}
        </div>
        {choosingDate && (
          <label className={styles.row}>
            <span className={styles.rowKey}>{t('gift_card_date_label')}</span>
            <input
              aria-invalid={!!errors.deliverAt}
              className={styles.rowValue}
              max={max}
              min={min}
              onChange={(event) => {
                if (!event.currentTarget.value) return;
                const next = { ...draft, deliverAt: giftCardDeliverAt(event.currentTarget.value) };
                setDraft(next);
                onChange(next);
              }}
              required
              type="date"
              value={giftCardDeliveryDay(draft.deliverAt)}
            />
          </label>
        )}
        {errors.deliverAt && <p className={styles.error}>{t(errors.deliverAt)}</p>}
      </div>
    </>
  );
}
